// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/* Admin Blog Automatisering - Potential Blogs workflow management for N8N automation
   - Super admins can change blog status via dropdown with badge display
   - Status dropdown shows only badge with caret icon (hover background on dropdown)
   - When changing from "approved" to any other status, automatically deletes the published blog from blogs table
   - Clears published_blog_id when reverting from approved status
   - Statistics displayed at the top with compact styling
   - "Nieuwe potentiële blog toevoegen" is collapsible and collapsed by default
   - "Alle potentiële blogs" section auto-expanded by default
   - Keywords field properly saved to database on create and edit
   - Removed "Beschrijving" column from table for cleaner view
   - Edit functionality for concept/ready blogs (title, description, keywords, prompt details, related products, thumbnail)
   - Redundant status action buttons removed (edit replaces them)
   - ExternalLink button navigates to admin blog overview page
   - resetBlogToConcept(id) function resets a potential blog to "Concept" status and clears all generated fields
   - Product selection modal to add related products (full URLs with base domain for internal linking)
   - Prompt details field for additional context to N8N
   - Product modal works in both create and edit contexts
   - Preview functionality: Open preview in new tab and copy preview URL for blogs with generated content
*/

import { sanitizeHtml } from '@/lib/sanitize-html';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, Upload, FileText, Clock, CircleCheck as CheckCircle2, Circle as XCircle, Loader as Loader2, Eye, ChevronDown, ChevronUp, ExternalLink, Search, CreditCard as Edit, Link as LinkIcon, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { marked } from 'marked';

interface PotentialBlog {
  id: string;
  title: string;
  slug: string | null;
  description: string;
  thumbnail_storage_path: string | null;
  status: 'concept' | 'ready' | 'processing' | 'pending_approval' | 'approved' | 'rejected';
  generated_content: string | null;
  n8n_processed_at: string | null;
  telegram_message_id: string | null;
  approval_response_at: string | null;
  rejection_reason: string | null;
  published_blog_id: string | null;
  keywords: string | null;
  related_products: { url: string; name: string }[] | null;
  prompt_details: string | null;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: string;
  name: string;
  base_slug: string;
}

export default function AdminBlogAutomatisering() {
  const [potentialBlogs, setPotentialBlogs] = useState<PotentialBlog[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [blogTitle, setBlogTitle] = useState('');
  const [blogSlug, setBlogSlug] = useState('');
  const [blogDescription, setBlogDescription] = useState('');
  const [blogStatus, setBlogStatus] = useState<'concept' | 'ready'>('concept');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewingBlog, setViewingBlog] = useState<PotentialBlog | null>(null);
  const [editingBlog, setEditingBlog] = useState<PotentialBlog | null>(null);
  const [deleteBlogId, setDeleteBlogId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllBlogs, setShowAllBlogs] = useState(true);
  const [showNewBlogForm, setShowNewBlogForm] = useState(false);
  const [blogKeywords, setBlogKeywords] = useState('');
  const [promptDetails, setPromptDetails] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<{ url: string; name: string }[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editPromptDetails, setEditPromptDetails] = useState('');
  const [editSelectedProducts, setEditSelectedProducts] = useState<{ url: string; name: string }[]>([]);
  const [editThumbnailFile, setEditThumbnailFile] = useState<File | null>(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState<string | null>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadPotentialBlogs();
    loadProducts();

    const subscription = supabase
      .channel('potential_blogs_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'potential_blogs' },
        () => {
          loadPotentialBlogs();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadPotentialBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('potential_blogs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPotentialBlogs(data || []);
    } catch (error) {
      console.error('Error loading potential blogs:', error);
      toast.error('Fout bij laden van potentiële blogs');
    }
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, base_slug')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Fout bij laden van producten');
    }
  };

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setBlogTitle(title);
    if (!blogSlug) {
      setBlogSlug(generateSlug(title));
    }
  };

  const handleEditTitleChange = (title: string) => {
    setEditTitle(title);
    if (!editSlug) {
      setEditSlug(generateSlug(title));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selecteer een afbeeldingsbestand');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Afbeelding moet kleiner zijn dan 5MB');
      return;
    }

    setThumbnailFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadThumbnail = async (): Promise<string | null> => {
    if (!thumbnailFile) return null;

    const fileExt = thumbnailFile.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('blog-images')
      .upload(filePath, thumbnailFile, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    return filePath;
  };

  const createPotentialBlog = async () => {
    if (!blogTitle.trim()) {
      toast.error('Voer een titel in');
      return;
    }

    if (!blogSlug.trim()) {
      toast.error('Voer een slug in');
      return;
    }

    if (!blogDescription.trim()) {
      toast.error('Voer een beschrijving in');
      return;
    }

    if (!thumbnailFile) {
      toast.error('Upload een thumbnail afbeelding');
      return;
    }

    setUploading(true);
    try {
      const thumbnailPath = await uploadThumbnail();

      const { error } = await supabase.from('potential_blogs').insert({
        title: blogTitle.trim(),
        slug: blogSlug.trim(),
        description: blogDescription.trim(),
        thumbnail_storage_path: thumbnailPath,
        status: blogStatus,
        keywords: blogKeywords.trim() || null,
        prompt_details: promptDetails.trim() || null,
        related_products: selectedProducts.length > 0 ? selectedProducts : null,
      });

      if (error) throw error;

      toast.success('Potentiële blog toegevoegd');
      setBlogTitle('');
      setBlogSlug('');
      setBlogDescription('');
      setBlogKeywords('');
      setPromptDetails('');
      setSelectedProducts([]);
      setBlogStatus('concept');
      setThumbnailFile(null);
      setThumbnailPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      loadPotentialBlogs();
    } catch (error) {
      console.error('Error creating potential blog:', error);
      toast.error('Fout bij toevoegen potentiële blog');
    } finally {
      setUploading(false);
    }
  };

  const updateBlogStatus = async (id: string, newStatus: PotentialBlog['status']) => {
    setLoading(true);
    try {
      const blog = potentialBlogs.find((b) => b.id === id);

      if (blog?.status === 'approved' && newStatus !== 'approved' && blog.published_blog_id) {
        const { error: deleteBlogError } = await supabase
          .from('blogs')
          .delete()
          .eq('id', blog.published_blog_id);

        if (deleteBlogError) {
          console.error('Error deleting published blog:', deleteBlogError);
          toast.error('Fout bij verwijderen gepubliceerde blog');
          return;
        }

        toast.success('Gepubliceerde blog verwijderd');
      }

      const { error } = await supabase
        .from('potential_blogs')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
          published_blog_id: newStatus !== 'approved' ? null : blog?.published_blog_id
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Status bijgewerkt');
      loadPotentialBlogs();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fout bij bijwerken status');
    } finally {
      setLoading(false);
    }
  };

  const resetBlogToConcept = async (id: string) => {
    setLoading(true);
    try {
      const blog = potentialBlogs.find((b) => b.id === id);

      if (blog?.status === 'approved' && blog.published_blog_id) {
        const { error: deleteBlogError } = await supabase
          .from('blogs')
          .delete()
          .eq('id', blog.published_blog_id);

        if (deleteBlogError) {
          console.error('Error deleting published blog:', deleteBlogError);
          toast.error('Fout bij verwijderen gepubliceerde blog');
          return;
        }

        toast.success('Gepubliceerde blog verwijderd');
      }

      const { error } = await supabase
        .from('potential_blogs')
        .update({
          status: 'concept',
          updated_at: new Date().toISOString(),
          published_blog_id: null,
          generated_content: null,
          n8n_processed_at: null,
          telegram_message_id: null,
          approval_response_at: null,
          rejection_reason: null
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Status gereset naar Concept');
      loadPotentialBlogs();
    } catch (error) {
      console.error('Error resetting to concept:', error);
      toast.error('Fout bij resetten naar Concept');
    } finally {
      setLoading(false);
    }
  };

  const deletePotentialBlog = async (id: string) => {
    setLoading(true);
    try {
      const blog = potentialBlogs.find((b) => b.id === id);

      if (blog?.thumbnail_storage_path) {
        await supabase.storage.from('blog-images').remove([blog.thumbnail_storage_path]);
      }

      const { error } = await supabase.from('potential_blogs').delete().eq('id', id);

      if (error) throw error;

      toast.success('Potentiële blog verwijderd');
      setDeleteBlogId(null);
      loadPotentialBlogs();
    } catch (error) {
      console.error('Error deleting potential blog:', error);
      toast.error('Fout bij verwijderen potentiële blog');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (blog: PotentialBlog) => {
    setEditingBlog(blog);
    setEditTitle(blog.title);
    setEditSlug(blog.slug || generateSlug(blog.title));
    setEditDescription(blog.description);
    setEditKeywords(blog.keywords || '');
    setEditPromptDetails(blog.prompt_details || '');
    setEditSelectedProducts(blog.related_products || []);
    setEditThumbnailFile(null);
    setEditThumbnailPreview(blog.thumbnail_storage_path ? getThumbnailUrl(blog.thumbnail_storage_path) : null);
  };

  const handleEditFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selecteer een afbeeldingsbestand');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Afbeelding moet kleiner zijn dan 5MB');
      return;
    }

    setEditThumbnailFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const updatePotentialBlog = async () => {
    if (!editingBlog) return;

    if (!editTitle.trim()) {
      toast.error('Voer een titel in');
      return;
    }

    if (!editSlug.trim()) {
      toast.error('Voer een slug in');
      return;
    }

    if (!editDescription.trim()) {
      toast.error('Voer een beschrijving in');
      return;
    }

    setUploading(true);
    try {
      let thumbnailPath = editingBlog.thumbnail_storage_path;

      if (editThumbnailFile) {
        if (editingBlog.thumbnail_storage_path) {
          await supabase.storage.from('blog-images').remove([editingBlog.thumbnail_storage_path]);
        }

        const fileExt = editThumbnailFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(filePath, editThumbnailFile, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) throw uploadError;
        thumbnailPath = filePath;
      }

      const { error } = await supabase
        .from('potential_blogs')
        .update({
          title: editTitle.trim(),
          slug: editSlug.trim(),
          description: editDescription.trim(),
          keywords: editKeywords.trim() || null,
          prompt_details: editPromptDetails.trim() || null,
          related_products: editSelectedProducts.length > 0 ? editSelectedProducts : null,
          thumbnail_storage_path: thumbnailPath,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingBlog.id);

      if (error) throw error;

      toast.success('Potentiële blog bijgewerkt');
      setEditingBlog(null);
      loadPotentialBlogs();
    } catch (error) {
      console.error('Error updating potential blog:', error);
      toast.error('Fout bij bijwerken potentiële blog');
    } finally {
      setUploading(false);
    }
  };

  const getThumbnailUrl = (path: string | null) => {
    if (!path) return null;
    const { data } = supabase.storage.from('blog-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const copyPreviewUrl = (blog: PotentialBlog) => {
    const slug = blog.slug;
    if (!slug) {
      toast.error('Geen slug beschikbaar');
      return;
    }
    const previewUrl = `${window.location.origin}/preview-blog/${slug}`;
    navigator.clipboard.writeText(previewUrl);
    toast.success('Preview URL gekopieerd naar klembord');
  };

  const getStatusBadge = (status: PotentialBlog['status']) => {
    const configs = {
      concept: { label: 'Concept', className: 'bg-gray-100 text-gray-800', icon: FileText },
      ready: { label: 'In afwachting van N8N', className: 'bg-[#e7e1d6] text-[#1e2b22]', icon: Clock },
      processing: { label: 'Verwerken', className: 'bg-[#f5eede] text-[#c8a94a]', icon: Loader2 },
      pending_approval: { label: 'Ter goedkeuring', className: 'bg-[#eee0cf] text-[#7a6450]', icon: Clock },
      approved: { label: 'Goedgekeurd', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      rejected: { label: 'Afgewezen', className: 'bg-red-100 text-red-800', icon: XCircle },
    };

    const config = configs[status];
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredBlogs = potentialBlogs.filter((blog) => {
    if (statusFilter !== 'all' && blog.status !== statusFilter) return false;
    if (searchQuery && !blog.title.toLowerCase().includes(searchQuery.toLowerCase()) && !blog.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const pendingBlogs = potentialBlogs.filter((b) => b.status === 'pending_approval');

  const statusCounts = {
    concept: potentialBlogs.filter((b) => b.status === 'concept').length,
    ready: potentialBlogs.filter((b) => b.status === 'ready').length,
    processing: potentialBlogs.filter((b) => b.status === 'processing').length,
    pending_approval: potentialBlogs.filter((b) => b.status === 'pending_approval').length,
    approved: potentialBlogs.filter((b) => b.status === 'approved').length,
    rejected: potentialBlogs.filter((b) => b.status === 'rejected').length,
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
          <Breadcrumb pageName="Blog automatisering" />
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Blog automatisering</h1>
            <p className="text-gray-600">
              Beheer potentiële blogs en keywords voor N8N blog automatisering met Telegram goedkeuringen
            </p>
          </div>

          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" />
                Statistieken
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-gray-900">{statusCounts.concept}</div>
                  <div className="text-xs text-gray-600">Concept</div>
                </div>
                <div className="text-center p-3 bg-[#e7e1d6]/40 rounded-lg">
                  <div className="text-xl font-bold text-[#1e2b22]">{statusCounts.ready}</div>
                  <div className="text-xs text-[#5a6552]">In afwachting</div>
                </div>
                <div className="text-center p-3 bg-[#f5eede] rounded-lg">
                  <div className="text-xl font-bold text-[#c8a94a]">{statusCounts.processing}</div>
                  <div className="text-xs text-[#8a7a3a]">Verwerken</div>
                </div>
                <div className="text-center p-3 bg-[#eee0cf] rounded-lg">
                  <div className="text-xl font-bold text-[#7a6450]">{statusCounts.pending_approval}</div>
                  <div className="text-xs text-[#7a6450]">Ter goedkeuring</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-900">{statusCounts.approved}</div>
                  <div className="text-xs text-green-700">Goedgekeurd</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-xl font-bold text-red-900">{statusCounts.rejected}</div>
                  <div className="text-xs text-red-700">Afgewezen</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Collapsible open={showNewBlogForm} onOpenChange={setShowNewBlogForm}>
            <Card className="mb-6">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Plus className="w-5 h-5" />
                        Nieuwe potentiële blog toevoegen
                      </CardTitle>
                      <CardDescription>
                        Voeg een blog idee toe die N8N zal uitwerken. Na verwerking krijg je een goedkeuringsverzoek via Telegram.
                      </CardDescription>
                    </div>
                    {showNewBlogForm ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
              <div className="space-y-2">
                <Label htmlFor="blog-title">
                  Titel <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="blog-title"
                  placeholder="Bijv: De beste haagplanten voor een groene tuin"
                  value={blogTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  disabled={uploading}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">{blogTitle.length}/200 karakters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="blog-slug">
                  URL Slug <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="blog-slug"
                    value={blogSlug}
                    onChange={(e) => setBlogSlug(e.target.value)}
                    placeholder="automatisch-gegenereerd"
                    className="flex-1"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const fullUrl = `https://buitnplanten.nl/preview-blog/${blogSlug || 'slug'}`;
                      navigator.clipboard.writeText(fullUrl);
                      toast.success('Preview URL gekopieerd naar klembord');
                    }}
                    title="Kopieer preview URL"
                    disabled={uploading}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Preview URL: https://buitnplanten.nl/preview-blog/{blogSlug || 'slug'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="blog-description">
                  Beschrijving <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="blog-description"
                  placeholder="Geef een korte beschrijving van wat de blog moet bevatten. N8N gebruikt dit om de volledige content te genereren..."
                  value={blogDescription}
                  onChange={(e) => setBlogDescription(e.target.value)}
                  disabled={uploading}
                  rows={4}
                  maxLength={1000}
                />
                <p className="text-xs text-gray-500">{blogDescription.length}/1000 karakters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="blog-keywords">
                  Keywords (optioneel)
                </Label>
                <Input
                  id="blog-keywords"
                  placeholder="Bijv: Haagplanten, Tuinonderhoud, Seizoensbeplanting (komma gescheiden)"
                  value={blogKeywords}
                  onChange={(e) => setBlogKeywords(e.target.value)}
                  disabled={uploading}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">Gebruik komma's om meerdere keywords te scheiden</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt-details">
                  Extra prompt details (optioneel)
                </Label>
                <Textarea
                  id="prompt-details"
                  placeholder="Voeg extra instructies of context toe voor de AI bij het genereren van de blog..."
                  value={promptDetails}
                  onChange={(e) => setPromptDetails(e.target.value)}
                  disabled={uploading}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500">{promptDetails.length}/500 karakters</p>
              </div>

              <div className="space-y-2">
                <Label>
                  Gerelateerde producten (optioneel)
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowProductModal(true)}
                    disabled={uploading}
                    className="w-full"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Producten selecteren ({selectedProducts.length})
                  </Button>
                </div>
                {selectedProducts.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {selectedProducts.map((product, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 p-2 bg-gray-50 rounded border"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 mb-0.5">{product.name}</div>
                          <div className="text-xs text-gray-600 font-mono break-all">{product.url}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedProducts(selectedProducts.filter((_, i) => i !== index))}
                          className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Deze producten worden als interne links in de blog gebruikt</p>
              </div>

              <div className="space-y-2">
                <Label>
                  Thumbnail afbeelding <span className="text-red-500">*</span>
                </Label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-jv-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {thumbnailPreview ? (
                    <div className="space-y-2">
                      <img
                        src={thumbnailPreview}
                        alt="Preview"
                        className="max-h-48 mx-auto rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setThumbnailFile(null);
                          setThumbnailPreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        Verwijderen
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 mx-auto text-gray-400" />
                      <p className="text-gray-600">Klik om een afbeelding te uploaden</p>
                      <p className="text-xs text-gray-500">PNG, JPG, WEBP (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <RadioGroup value={blogStatus} onValueChange={(v) => setBlogStatus(v as 'concept' | 'ready')}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="concept" id="concept" />
                    <Label htmlFor="concept" className="cursor-pointer">
                      Concept - Bewaar als concept (N8N verwerkt niet)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ready" id="ready" />
                    <Label htmlFor="ready" className="cursor-pointer">
                      Klaar voor verwerking - N8N pikt deze op en genereert content
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                onClick={createPotentialBlog}
                disabled={uploading || !blogTitle.trim() || !blogDescription.trim() || !thumbnailFile}
                className="w-full bg-jv-primary hover:bg-jv-primary/90"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploaden...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Potentiële blog toevoegen
                  </>
                )}
              </Button>
            </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {pendingBlogs.length > 0 && (
            <Card className="mb-8 border-[#e7e1d6] bg-[#eee0cf]/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#7a6450]">
                  <Clock className="w-5 h-5" />
                  Ter goedkeuring ({pendingBlogs.length})
                </CardTitle>
                <CardDescription className="text-[#5a6552]">
                  Deze blogs zijn verwerkt door N8N en wachten op jouw goedkeuring via Telegram
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {pendingBlogs.map((blog) => (
                    <Card key={blog.id} className="bg-white">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {blog.thumbnail_storage_path && (
                            <img
                              src={getThumbnailUrl(blog.thumbnail_storage_path)!}
                              alt={blog.title}
                              className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1 truncate">{blog.title}</h3>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{blog.description}</p>
                            <div className="flex items-center justify-between">
                              <Badge className="bg-[#eee0cf] text-[#7a6450] text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Wacht op Telegram
                              </Badge>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setViewingBlog(blog)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {blog.generated_content && blog.slug && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      asChild
                                    >
                                      <a
                                        href={`/preview-blog/${blog.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </a>
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyPreviewUrl(blog)}
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Collapsible open={showAllBlogs} onOpenChange={setShowAllBlogs}>
            <Card className="mb-8">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Alle potentiële blogs ({potentialBlogs.length})
                    </CardTitle>
                    {showAllBlogs ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <CardDescription>Bekijk en beheer alle potentiële blogs</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Zoek op titel of beschrijving..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Filter op status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle statussen</SelectItem>
                        <SelectItem value="concept">Concept</SelectItem>
                        <SelectItem value="ready">In afwachting van N8N</SelectItem>
                        <SelectItem value="processing">Verwerken</SelectItem>
                        <SelectItem value="pending_approval">Ter goedkeuring</SelectItem>
                        <SelectItem value="approved">Goedgekeurd</SelectItem>
                        <SelectItem value="rejected">Afgewezen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {filteredBlogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-lg font-medium">Geen potentiële blogs gevonden</p>
                      <p className="text-sm">Voeg je eerste blog idee toe om te beginnen</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Thumbnail</TableHead>
                            <TableHead>Titel</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Aangemaakt</TableHead>
                            <TableHead className="text-right">Acties</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBlogs.map((blog) => (
                            <TableRow key={blog.id}>
                              <TableCell>
                                {blog.thumbnail_storage_path && (
                                  <img
                                    src={getThumbnailUrl(blog.thumbnail_storage_path)!}
                                    alt={blog.title}
                                    className="w-16 h-16 rounded object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell className="font-medium max-w-xs">
                                <div className="truncate">{blog.title}</div>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={blog.status}
                                  onValueChange={(value) => updateBlogStatus(blog.id, value as PotentialBlog['status'])}
                                  disabled={loading}
                                >
                                  <SelectTrigger className="w-auto border-0 bg-transparent p-0 h-auto focus:ring-0 focus:ring-offset-0 gap-1 hover:bg-gray-100 rounded px-1 transition-colors">
                                    <span className="pointer-events-none">{getStatusBadge(blog.status)}</span>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="concept">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-3 h-3" />
                                        Concept
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="ready">
                                      <div className="flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        In afwachting van N8N
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="processing">
                                      <div className="flex items-center gap-2">
                                        <Loader2 className="w-3 h-3" />
                                        Verwerken
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="pending_approval">
                                      <div className="flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        Ter goedkeuring
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="approved">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-3 h-3" />
                                        Goedgekeurd
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="rejected">
                                      <div className="flex items-center gap-2">
                                        <XCircle className="w-3 h-3" />
                                        Afgewezen
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-gray-600 text-sm whitespace-nowrap">
                                {new Date(blog.created_at).toLocaleDateString('nl-NL')}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setViewingBlog(blog)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {blog.generated_content && blog.slug && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        asChild
                                      >
                                        <a
                                          href={`/preview-blog/${blog.slug}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center justify-center"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => copyPreviewUrl(blog)}
                                      >
                                        <Copy className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  {(blog.status === 'concept' || blog.status === 'ready') && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEditDialog(blog)}
                                        disabled={loading}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setDeleteBlogId(blog.id)}
                                        disabled={loading}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  {blog.status === 'approved' && blog.published_blog_id && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      asChild
                                    >
                                      <a
                                        href={`/kh-admin/blogs`}
                                      >
                                        <ExternalLink className="w-4 h-4 text-green-600" />
                                      </a>
                                    </Button>
                                  )}
                                  {blog.status === 'rejected' && blog.rejection_reason && (
                                    <div className="text-xs text-red-600 max-w-xs truncate" title={blog.rejection_reason}>
                                      {blog.rejection_reason}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

        </div>

      <Dialog open={!!viewingBlog} onOpenChange={() => setViewingBlog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingBlog?.title}</DialogTitle>
            <DialogDescription>
              Status: {viewingBlog && getStatusBadge(viewingBlog.status)}
            </DialogDescription>
          </DialogHeader>

          {viewingBlog && (
            <div className="space-y-6">
              {viewingBlog.thumbnail_storage_path && (
                <div>
                  <h3 className="font-semibold mb-2">Thumbnail</h3>
                  <img
                    src={getThumbnailUrl(viewingBlog.thumbnail_storage_path)!}
                    alt={viewingBlog.title}
                    className="w-full max-h-64 object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Originele beschrijving</h3>
                <p className="text-gray-700">{viewingBlog.description}</p>
              </div>

              {viewingBlog.keywords && (
                <div>
                  <h3 className="font-semibold mb-2">Keywords</h3>
                  <p className="text-gray-700">{viewingBlog.keywords}</p>
                </div>
              )}

              {viewingBlog.prompt_details && (
                <div>
                  <h3 className="font-semibold mb-2">Extra prompt details</h3>
                  <p className="text-gray-700">{viewingBlog.prompt_details}</p>
                </div>
              )}

              {viewingBlog.related_products && viewingBlog.related_products.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Gerelateerde producten</h3>
                  <div className="space-y-2">
                    {viewingBlog.related_products.map((product, index) => (
                      <div
                        key={index}
                        className="p-2 bg-gray-50 rounded border"
                      >
                        <div className="font-medium text-sm text-gray-900 mb-1">{product.name}</div>
                        <div className="text-xs text-gray-600 font-mono break-all">{product.url}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingBlog.generated_content && (
                <div>
                  <h3 className="font-semibold mb-2">Gegenereerde content</h3>
                  <div
                    className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(marked(viewingBlog.generated_content) as string),
                    }}
                  />
                </div>
              )}

              {viewingBlog.rejection_reason && (
                <div>
                  <h3 className="font-semibold mb-2 text-red-600">Reden van afwijzing</h3>
                  <p className="text-red-700 bg-red-50 p-3 rounded">{viewingBlog.rejection_reason}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-semibold">Aangemaakt:</span>{' '}
                  {new Date(viewingBlog.created_at).toLocaleString('nl-NL')}
                </div>
                {viewingBlog.n8n_processed_at && (
                  <div>
                    <span className="font-semibold">Verwerkt door N8N:</span>{' '}
                    {new Date(viewingBlog.n8n_processed_at).toLocaleString('nl-NL')}
                  </div>
                )}
                {viewingBlog.approval_response_at && (
                  <div>
                    <span className="font-semibold">Goedkeuring ontvangen:</span>{' '}
                    {new Date(viewingBlog.approval_response_at).toLocaleString('nl-NL')}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingBlog} onOpenChange={() => setEditingBlog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Potentiële blog bewerken</DialogTitle>
            <DialogDescription>
              Bewerk de titel, beschrijving, keywords en thumbnail van deze potentiële blog
            </DialogDescription>
          </DialogHeader>

          {editingBlog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">
                  Titel <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-title"
                  value={editTitle}
                  onChange={(e) => handleEditTitleChange(e.target.value)}
                  disabled={uploading}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">{editTitle.length}/200 karakters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-slug">
                  URL Slug <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-slug"
                    value={editSlug}
                    onChange={(e) => setEditSlug(e.target.value)}
                    placeholder="automatisch-gegenereerd"
                    className="flex-1"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const fullUrl = `https://buitnplanten.nl/preview-blog/${editSlug || 'slug'}`;
                      navigator.clipboard.writeText(fullUrl);
                      toast.success('Preview URL gekopieerd naar klembord');
                    }}
                    title="Kopieer preview URL"
                    disabled={uploading}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Preview URL: https://buitnplanten.nl/preview-blog/{editSlug || 'slug'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-description">
                  Beschrijving <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  disabled={uploading}
                  rows={4}
                  maxLength={1000}
                />
                <p className="text-xs text-gray-500">{editDescription.length}/1000 karakters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-keywords">
                  Keywords (optioneel)
                </Label>
                <Input
                  id="edit-keywords"
                  placeholder="Bijv: Haagplanten, Tuinonderhoud, Seizoensbeplanting (komma gescheiden)"
                  value={editKeywords}
                  onChange={(e) => setEditKeywords(e.target.value)}
                  disabled={uploading}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">Gebruik komma's om meerdere keywords te scheiden</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-prompt-details">
                  Extra prompt details (optioneel)
                </Label>
                <Textarea
                  id="edit-prompt-details"
                  placeholder="Voeg extra instructies of context toe voor de AI bij het genereren van de blog..."
                  value={editPromptDetails}
                  onChange={(e) => setEditPromptDetails(e.target.value)}
                  disabled={uploading}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500">{editPromptDetails.length}/500 karakters</p>
              </div>

              <div className="space-y-2">
                <Label>
                  Gerelateerde producten (optioneel)
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowProductModal(true)}
                    disabled={uploading}
                    className="w-full"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Producten selecteren ({editSelectedProducts.length})
                  </Button>
                </div>
                {editSelectedProducts.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {editSelectedProducts.map((product, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 p-2 bg-gray-50 rounded border"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 mb-0.5">{product.name}</div>
                          <div className="text-xs text-gray-600 font-mono break-all">{product.url}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditSelectedProducts(editSelectedProducts.filter((_, i) => i !== index))}
                          className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Deze producten worden als interne links in de blog gebruikt</p>
              </div>

              <div className="space-y-2">
                <Label>Thumbnail afbeelding</Label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-jv-primary transition-colors"
                  onClick={() => editFileInputRef.current?.click()}
                >
                  {editThumbnailPreview ? (
                    <div className="space-y-2">
                      <img
                        src={editThumbnailPreview}
                        alt="Preview"
                        className="max-h-48 mx-auto rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditThumbnailFile(null);
                          setEditThumbnailPreview(editingBlog.thumbnail_storage_path ? getThumbnailUrl(editingBlog.thumbnail_storage_path) : null);
                          if (editFileInputRef.current) {
                            editFileInputRef.current.value = '';
                          }
                        }}
                      >
                        {editThumbnailFile ? 'Nieuwe afbeelding annuleren' : 'Verwijderen'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 mx-auto text-gray-400" />
                      <p className="text-gray-600">Klik om een afbeelding te uploaden</p>
                      <p className="text-xs text-gray-500">PNG, JPG, WEBP (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleEditFileSelect}
                  className="hidden"
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditingBlog(null)}
                  disabled={uploading}
                >
                  Annuleren
                </Button>
                <Button
                  onClick={updatePotentialBlog}
                  disabled={uploading || !editTitle.trim() || !editDescription.trim()}
                  className="bg-jv-primary hover:bg-jv-primary/90"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    'Wijzigingen opslaan'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteBlogId} onOpenChange={() => setDeleteBlogId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potentiële blog verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze potentiële blog wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteBlogId && deletePotentialBlog(deleteBlogId)}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Producten selecteren</DialogTitle>
            <DialogDescription>
              Selecteer producten die in de blog vermeld moeten worden (voor interne links)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Zoek producten..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
              {products
                .filter((product) =>
                  product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
                )
                .map((product) => {
                  const fullUrl = `https://buitnplanten.nl/${product.base_slug}`;
                  const currentProducts = editingBlog ? editSelectedProducts : selectedProducts;
                  const isSelected = currentProducts.some((p) => p.url === fullUrl);
                  return (
                    <div
                      key={product.id}
                      className={`p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-jv-primary/5' : ''
                      }`}
                      onClick={() => {
                        if (editingBlog) {
                          if (isSelected) {
                            setEditSelectedProducts(
                              editSelectedProducts.filter((p) => p.url !== fullUrl)
                            );
                          } else {
                            setEditSelectedProducts([
                              ...editSelectedProducts,
                              { url: fullUrl, name: product.name },
                            ]);
                          }
                        } else {
                          if (isSelected) {
                            setSelectedProducts(
                              selectedProducts.filter((p) => p.url !== fullUrl)
                            );
                          } else {
                            setSelectedProducts([
                              ...selectedProducts,
                              { url: fullUrl, name: product.name },
                            ]);
                          }
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500 font-mono break-all">{fullUrl}</div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-jv-primary flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              {products.filter((product) =>
                product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
              ).length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Geen producten gevonden</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-gray-600">
                {editingBlog ? editSelectedProducts.length : selectedProducts.length} product(en) geselecteerd
              </p>
              <Button onClick={() => setShowProductModal(false)} className="bg-jv-primary hover:bg-jv-primary/90">
                Klaar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
